import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styles from './ChatPage.module.css';
import { SideNavigationMenu } from '../components/library/side-navigation-menu/SideNavigationMenu';
import { ChatHeader } from '../components/ChatHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { LocalSessionProvider } from '../contexts/LocalSessionContext';
import { NewChatPage } from './NewChatPage';
import { SessionChatPage } from './SessionChatPage';
import { DeleteConfirmationBanner } from '../components/library/banners/ConfirmationBanner';
import { ChatInput } from '../components/library/chat-interface/ChatInput';

export const ChatPage: React.FC = () => {
  const { chatHash } = useParams<{ chatHash: string }>();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  
  const { theme } = useTheme();
  const { isLoading } = useAuth();

  // Add chat-page class to body for fixed layout
  useEffect(() => {
    document.body.classList.add('chat-page');
    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  // Callback for child pages to report scroll direction
  const handleScrollDirectionChange = (isScrollingDown: boolean) => {
    setIsHeaderHidden(isScrollingDown);
  };

  const handleSidebarToggle = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  };

  // Always render the main interface, conditionally show loading overlay
  return (
    <div className={styles.chatPageContainer} data-theme={theme}>
      <DeleteConfirmationBanner />
      <SideNavigationMenu
        isOpen={true}
        isCollapsed={sidebarCollapsed}
        onCollapse={handleSidebarToggle}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />

      <ChatHeader
        sidebarCollapsed={sidebarCollapsed}
        onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isHidden={isHeaderHidden}
      />

      {isLoading ? (
        // Show loading overlay during auth initialization
        <div className={`${styles.chatContainer} ${sidebarCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}>
          <div className={styles.chatContent}>
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}>
                <div className={styles.spinner}></div>
                <div className={styles.loadingText}>Loading...</div>
              </div>
            </div>
          </div>
          <div className={styles.chatInputContainer}>
            <ChatInput
              onSend={() => {}}
              isBottom={true}
              isProcessing={true}
              isFirstMessage={false}
              isSessionBusy={false}
            />
          </div>
        </div>
      ) : chatHash ? (
        <LocalSessionProvider key={chatHash}>
          <SessionChatPage
            chatHash={chatHash}
            sidebarCollapsed={sidebarCollapsed}
            onScrollDirectionChange={handleScrollDirectionChange}
          />
        </LocalSessionProvider>
      ) : (
        // Use location.key to force remount when navigating to /chat
        // This ensures NewChatPage resets its state after using replaceState
        <NewChatPage
          key={location.key}
          sidebarCollapsed={sidebarCollapsed}
          onScrollDirectionChange={handleScrollDirectionChange}
        />
      )}
    </div>
  );
};
